// src/components/dashboard/stats/TodaySessionsChart.tsx
// ✅ Tooltip via React state (position:fixed) — escapa de overflow:hidden
// ✅ Container maior, tooltip compacto em linha

import React, { useEffect, useState, useCallback } from 'react';
import { TodaySession } from '../types';

interface TodaySessionsChartProps {
    sessions: TodaySession[];
    animate: boolean;
}

interface TooltipState {
    visible: boolean;
    x: number;
    y: number;
    deckName: string;
    subdeckName: string;
    cards: number;
    duration: string;
}

const BAR_COL_WIDTH = 56;
const BAR_GAP = 10;
const TRACK_HEIGHT = 160;   // ← aumentado de 120 para 160
const BAR_WIDTH = 38;
const CONTAINER_PADDING = 28;

const TodaySessionsChart: React.FC<TodaySessionsChartProps> = ({ sessions, animate }) => {
    const [animated, setAnimated] = useState(false);
    const [tooltip, setTooltip] = useState<TooltipState>({
        visible: false, x: 0, y: 0, deckName: '', subdeckName: '', cards: 0, duration: '',
    });

    useEffect(() => {
        if (animate && !animated) setAnimated(true);
    }, [animate, animated]);

    const formatDuration = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return minutes > 0 ? `${minutes}min` : `${secs}s`;
    };

    const maxDuration = sessions.length > 0
        ? Math.max(...sessions.map(s => s.duration), 1)
        : 1;

    const totalWidth = sessions.length > 0
        ? sessions.length * BAR_COL_WIDTH + (sessions.length - 1) * BAR_GAP
        : 0;

    const handleMouseEnter = useCallback((
        e: React.MouseEvent<HTMLDivElement>,
        session: TodaySession
    ) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltip({
            visible: true,
            x: rect.left + rect.width / 2,
            y: rect.top,
            deckName: session.deckName,        // ← só o deck
            subdeckName: session.subdeckName ?? '',  // ← subdeck separado
            cards: session.cardsStudied,
            duration: formatDuration(session.duration),
        });
    }, []);

    const handleMouseLeave = useCallback(() => {
        setTooltip(t => ({ ...t, visible: false }));
    }, []);

    return (
        <>
            {/* Tooltip via portal — position:fixed, nunca cortado */}
            {tooltip.visible && (
                <div style={{
                    position: 'fixed',
                    left: tooltip.x,
                    top: tooltip.y - 12,
                    transform: 'translateX(-50%) translateY(-100%)',
                    background: 'var(--btn-primary)',
                    color: 'white',
                    padding: '7px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    zIndex: 99999,
                    boxShadow: '0 4px 12px var(--platform-a35)',
                    lineHeight: 1.4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                }}>
                    {/* Linha 1: nome do deck */}
                    <div style={{
                        fontSize: 12,
                        fontWeight: 700,
                        marginBottom: 2,
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                    }}>
                        {tooltip.deckName}
                    </div>

                    {/* Linha 2: nome do subdeck (só se existir) */}
                    {tooltip.subdeckName && (
                        <div style={{
                            fontSize: 11,
                            fontWeight: 500,
                            opacity: 0.85,
                            marginBottom: 4,
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                        }}>
                            {tooltip.subdeckName}
                        </div>
                    )}

                    {/* Linha 3: cards e duração — era "Linha 2", não muda */}
                    <div style={{
                        fontSize: 11,
                        opacity: 0.85,
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                    }}>
                        <span>{tooltip.cards} cards</span>
                        <span style={{ opacity: 0.5 }}>|</span>
                        <span>{tooltip.duration}</span>
                    </div>

                    {/* Seta para baixo */}
                    <div style={{
                        position: 'absolute',
                        bottom: -6,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid var(--btn-primary)',
                    }} />
                </div>
            )}

            {/* Card do gráfico */}
            <div style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--platform-a20)',
                borderRadius: 12,
                padding: CONTAINER_PADDING,
                marginBottom: 32,
                boxShadow: 'var(--shadow-sm)',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                // overflow:hidden REMOVIDO do container externo
                // para o tooltip não ser cortado.
                // A contenção lateral é garantida pelo scroll interno.
                minWidth: 0,
            }}>
                <h3 style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    margin: '0 0 20px',
                }}>
                    Sessões de Estudo Hoje
                </h3>

                {sessions.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                        Nenhuma sessão registrada hoje
                    </p>
                ) : (
                    <div style={{
                        overflowX: 'auto',
                        overflowY: 'visible',   // ← visible para tooltip aparecer acima
                        width: '100%',
                        boxSizing: 'border-box',
                        paddingBottom: 8,
                        paddingTop: 8,           // ← espaço para tooltip no topo
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: BAR_GAP,
                            width: totalWidth,
                            height: TRACK_HEIGHT,
                            alignItems: 'flex-end',
                            padding: '12px 4px 0',
                            boxSizing: 'border-box',
                        }}>
                            {sessions.map((session, index) => {
                                const heightPct = Math.max(
                                    (session.duration / maxDuration) * 88, // ← máximo 88% deixa espaço no topo
                                    8
                                );
                                const delay = animated ? `${index * 0.06}s` : '0s';

                                return (
                                    <div key={index} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 4,
                                        width: BAR_COL_WIDTH,
                                        minWidth: BAR_COL_WIDTH,
                                        maxWidth: BAR_COL_WIDTH,
                                        height: '100%',
                                        justifyContent: 'flex-end',
                                        flexShrink: 0,
                                    }}>
                                        {/* Barra clicável com hover handler */}
                                        <div
                                            onMouseEnter={(e) => handleMouseEnter(e, session)}
                                            onMouseLeave={handleMouseLeave}
                                            style={{
                                                width: BAR_WIDTH,
                                                height: `${heightPct}%`,
                                                background: 'var(--btn-primary)',
                                                borderRadius: '4px 4px 0 0',
                                                minHeight: 8,
                                                flexShrink: 0,
                                                cursor: 'pointer',
                                                position: 'relative',
                                                animation: 'dsb-estx-barGrowUp 0.5s ease-out backwards',
                                                animationDelay: delay,
                                                transition: 'filter 0.15s ease, transform 0.15s ease',
                                            }}
                                            onMouseOver={e => {
                                                (e.currentTarget as HTMLDivElement).style.filter = 'brightness(1.15)';
                                                (e.currentTarget as HTMLDivElement).style.transform = 'scaleY(1.03)';
                                            }}
                                            onMouseOut={e => {
                                                (e.currentTarget as HTMLDivElement).style.filter = '';
                                                (e.currentTarget as HTMLDivElement).style.transform = '';
                                                setTooltip(t => ({ ...t, visible: false }));
                                            }}
                                        />
                                        <span style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: 'var(--btn-primary)',  // destaque em verde
                                            whiteSpace: 'nowrap',
                                            marginBottom: 2,
                                        }}>
                                        </span>
                                        <div /* barra — sem mudanças */ />
                                        <span style={{
                                            fontSize: 10,
                                            color: 'var(--text-secondary)',
                                            opacity: 0.7,
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {formatDuration(session.duration)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default TodaySessionsChart;